import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { EmptyState } from '../components/EmptyState';
import { Plus, MagnifyingGlass, Eye, Pencil, Buildings, Phone, Envelope, Upload, Trash, CheckSquare, Square } from '@phosphor-icons/react';
import { ViewToggle, useViewMode } from '../components/ViewToggle';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTenantPath } from '../lib/tenantPath';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import BulkUploadModal from '../components/BulkUploadModal';
import useAuthStore from '../store/authStore';
import useBulkSelection from '../hooks/useBulkSelection';
import BulkDeleteDialog, { BulkActionBar } from '../components/BulkActions';
import SummaryCards, { inr } from '../components/SummaryCards';
import OwnerFilter from '../components/OwnerFilter';
import PipelineToolbar from '../components/PipelineToolbar';
import { sortRows } from '../lib/pipelineFilters';
import { CurrencyInr, Star, Users } from '@phosphor-icons/react';

import { notify } from '../lib/notify';
import { PageSkeleton } from '../components/Skeletons';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Indian states organized by region (same as Leads)
const STATES_BY_REGION = {
  'South India': ['Andhra Pradesh', 'Karnataka', 'Kerala', 'Tamil Nadu', 'Telangana', 'Puducherry', 'Lakshadweep', 'Andaman and Nicobar Islands'],
  'North India': ['Delhi', 'Haryana', 'Himachal Pradesh', 'Jammu and Kashmir', 'Ladakh', 'Punjab', 'Rajasthan', 'Uttar Pradesh', 'Uttarakhand', 'Chandigarh'],
  'East India': ['Bihar', 'Jharkhand', 'Odisha', 'West Bengal'],
  'West India': ['Goa', 'Gujarat', 'Maharashtra', 'Dadra and Nagar Haveli and Daman and Diu'],
  'Central India': ['Chhattisgarh', 'Madhya Pradesh'],
  'Northeast India': ['Arunachal Pradesh', 'Assam', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland', 'Sikkim', 'Tripura']
};

const ALL_STATES = Object.entries(STATES_BY_REGION).flatMap(([region, states]) =>
  states.map(state => ({ state, region }))
);

const Customers = () => {
  const { user } = useAuthStore();
  const canBulkUpload = ['Vice President', 'President'].includes(user?.role);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [ownerFilter, setOwnerFilter] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useViewMode('customers', 'list');
  const [sortBy, setSortBy] = useState('created_desc');
  const navigate = useNavigate();
  const tp = useTenantPath();
  const location = useLocation();

  const emptyForm = {
    organisation_name: '',
    md_ceo_name: '',    poc_name: '',
    poc_designation: '',
    phone: '',
    email: '',
    address: '',
    country: 'India',
    city: '',
    state: '',
    gst_number: '',
    gst_exemption_code: '',
    pan_number: '',
    bank_accounts: [{ bank_name: '', account_number: '', ifsc_code: '', branch: '' }],
    industry_type: '',
    customer_role: 'End Buyer',   // iter96 — Dealer/Liaison used by Tender Participation flow
    website: '',
    notes: ''
  };

  const [formData, setFormData] = useState(emptyForm);
  const [gstVerifying, setGstVerifying] = useState(false);
  const [gstStatus, setGstStatus] = useState(null); // null | {valid, legal_name, ...}
  const [noGstMode, setNoGstMode] = useState(false);
  const [showExemptionModal, setShowExemptionModal] = useState(false);
  const [exemptionForm, setExemptionForm] = useState({ reason: '', notes: '' });
  const [submittingExemption, setSubmittingExemption] = useState(false);
  const [codeCheck, setCodeCheck] = useState(null); // {valid, reason, organisation_name}

  // If redirected from lead creation with pre-filled data
  const prefillFromLead = location.state?.fromLead;
  const returnToLead = location.state?.returnToLead;

  useEffect(() => {
    fetchCustomers();
    fetchSummary();
    if (prefillFromLead) {
      setFormData({
        ...emptyForm,
        organisation_name: prefillFromLead.company || '',
        poc_name: prefillFromLead.name || '',
        phone: prefillFromLead.phone || '',
        email: prefillFromLead.email || '',
        address: prefillFromLead.address || '',
        city: prefillFromLead.city || '',
        state: prefillFromLead.state || '',
        industry_type: prefillFromLead.customer_type || '',
      });
      setShowModal(true);
    }
  }, []);

  // Refetch when the owner filter changes (manager view).
  useEffect(() => {
    fetchCustomers();
    fetchSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerFilter]);

  const fetchCustomers = async () => {
    try {
      const params = ownerFilter.length ? `?owner_ids=${ownerFilter.join(',')}` : '';
      const response = await axios.get(`${API}/customers${params}`);
      setCustomers(response.data);
    } catch (error) {
      console.error('Error fetching customers:', error);
    } finally {
      setLoading(false);
    }
  };

  // Revenue rollup — attributed via *visible* leads pipeline, so a Sales
  // Exec's number reflects only their own book.
  const fetchSummary = async () => {
    setSummaryLoading(true);
    try {
      const r = await axios.get(`${API}/customers/summary`);
      setSummary(r.data);
    } catch (error) {
      console.error('Error fetching customers summary:', error);
      setSummary(null);
    } finally {
      setSummaryLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // In No-GST mode, require validated code
    if (noGstMode) {
      if (!formData.gst_exemption_code || !codeCheck?.valid) {
        notify('Please enter and validate your GST Exemption code before submitting.');
        return;
      }
    }
    try {
      const payload = {
        ...formData,
        gst_number: noGstMode ? '' : formData.gst_number,
        gst_exemption_code: noGstMode ? formData.gst_exemption_code : '',
      };
      const response = await axios.post(`${API}/customers`, payload);
      const newCustomer = response.data;
      fetchCustomers();
      setShowModal(false);
      setFormData(emptyForm);
      setGstStatus(null);
      setNoGstMode(false);
      setCodeCheck(null);

      // If from lead creation, redirect back to add lead with customer pre-selected
      if (returnToLead) {
        navigate(tp('/leads'), {
          state: {
            openAddLead: true,
            preselectedCustomer: newCustomer
          }
        });
      }
    } catch (error) {
      console.error('Error creating customer:', error);
      notify(error.response?.data?.detail || 'Error creating customer');
    }
  };

  const validateExemptionCode = async () => {
    const code = (formData.gst_exemption_code || '').trim().toUpperCase();
    if (!code) {
      setCodeCheck({ valid: false, reason: 'Code is required' });
      return;
    }
    try {
      const r = await axios.post(`${API}/gst-exemptions/validate-code`, { code });
      setCodeCheck(r.data);
    } catch (e) {
      setCodeCheck({ valid: false, reason: e.response?.data?.detail || 'Validation failed' });
    }
  };

  const submitExemptionRequest = async () => {
    if (!exemptionForm.reason.trim()) {
      notify('Please provide a reason');
      return;
    }
    setSubmittingExemption(true);
    try {
      await axios.post(`${API}/gst-exemptions`, {
        organisation_name: formData.organisation_name || exemptionForm.notes || 'Unspecified',
        reason: exemptionForm.reason.trim(),
        notes: exemptionForm.notes.trim(),
      });
      setShowExemptionModal(false);
      setExemptionForm({ reason: '', notes: '' });
      notify(
        user?.role === 'President'
          ? 'GST exemption auto-approved. Check Notifications for the bypass code.'
          : 'GST exemption request sent to your supervisor. You will receive the bypass code in Notifications once approved.'
      );
    } catch (e) {
      notify(e.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmittingExemption(false);
    }
  };

  const verifyGST = async (gstin) => {
    if (!gstin || gstin.length < 15) {
      setGstStatus(null);
      return;
    }
    setGstVerifying(true);
    try {
      const response = await axios.get(`${API}/gst/verify/${gstin.trim()}`);
      const data = response.data;
      setGstStatus(data);
      if (data.valid) {
        // Auto-fill state from GSTIN
        if (data.state) {
          setFormData(prev => ({ ...prev, state: data.state }));
        }
        // Auto-fill organisation name from legal name
        if (data.legal_name) {
          setFormData(prev => ({ ...prev, organisation_name: data.legal_name }));
        }
        // Auto-fill MD/CEO name from trade name (often contains proprietor/partner name)
        if (data.trade_name && data.trade_name !== data.legal_name) {
          setFormData(prev => ({ ...prev, md_ceo_name: data.trade_name }));
        }
        // Auto-fill PAN from GSTIN
        if (data.pan) {
          setFormData(prev => ({ ...prev, pan_number: data.pan }));
        }
        // Auto-fill address if available
        if (data.address) {
          setFormData(prev => ({ ...prev, address: data.address }));
        }
      }
    } catch (error) {
      setGstStatus({ valid: false, error: 'Could not verify GST number' });
    } finally {
      setGstVerifying(false);
    }
  };

  const handleStateChange = (selectedState) => {
    setFormData(prev => ({ ...prev, state: selectedState }));
  };

  const addBankAccount = () => {
    setFormData({
      ...formData,
      bank_accounts: [...formData.bank_accounts, { bank_name: '', account_number: '', ifsc_code: '', branch: '' }]
    });
  };

  const updateBankAccount = (index, field, value) => {
    const updated = [...formData.bank_accounts];
    updated[index][field] = value;
    setFormData({ ...formData, bank_accounts: updated });
  };

  const removeBankAccount = (index) => {
    if (formData.bank_accounts.length <= 1) return;
    setFormData({
      ...formData,
      bank_accounts: formData.bank_accounts.filter((_, i) => i !== index)
    });
  };

  const filteredCustomers = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    let list = customers;
    if (q) {
      list = list.filter(c => {
        const hay = [
          c.organisation_name, c.customer_code, c.gst_number, c.email, c.city,
          c.poc_name, c.phone, c.md_ceo_name, c.industry_type, c.state,
        ].filter(Boolean).join(' | ').toLowerCase();
        return hay.includes(q);
      });
    }
    // Sort — customers don't have close-date, so we use `total_revenue`
    // (populated by /customers when available) or fall back to created_at.
    return sortRows(list, sortBy, { valueKey: 'total_revenue', dateKey: 'updated_at' });
  }, [customers, searchTerm, sortBy]);

  const visibleIds = useMemo(() => filteredCustomers.map(c => c.id), [filteredCustomers]);
  const selection = useBulkSelection(visibleIds);
  const canBulkDelete = ['Vice President', 'President'].includes(user?.role);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const submitBulkDelete = async ({ reason }) => {
    setBulkDeleting(true);
    try {
      const r = await axios.post(`${API}/customers/bulk-delete`, {
        customer_ids: selection.selectedIds,
        reason,
      });
      notify(`✅ ${r.data.deleted} customer(s) deleted.`);
      setShowBulkDeleteDialog(false);
      selection.clear();
      await fetchCustomers();
    } catch (e) {
      notify(e.response?.data?.detail || 'Bulk delete failed');
    } finally { setBulkDeleting(false); }
  };

  if (loading) {
    return <PageSkeleton />;
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="customers-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6 lg:mb-8">
        <div>
          <h1 className="app-page-title" style={{ fontFamily: 'Outfit' }} data-testid="customers-title">
            Customer Database
          </h1>
          <p className="text-[#6D8E87]">Manage your customer information</p>
        </div>
        <div className="flex gap-3">
          {canBulkUpload && (
            <button onClick={() => setShowBulkModal(true)}
              className="flex items-center gap-2 px-5 py-3 border-2 border-[#00a69c] text-[#00a69c] rounded-full font-medium hover:bg-[#E2F3F0] transition"
              data-testid="bulk-customers-button">
              <Upload size={20} weight="bold" /> Bulk Upload
            </button>
          )}
          <button
            onClick={() => { setFormData(emptyForm); setGstStatus(null); setShowModal(true); }}
            className="btn-primary flex items-center gap-2"
            data-testid="add-customer-button"
          >
            <Plus size={20} weight="bold" />
            Add Customer
          </button>
        </div>
      </div>

      <div className="flex items-center justify-end mb-2">
        <OwnerFilter value={ownerFilter} onChange={setOwnerFilter} testid="customers-owner-filter" />
      </div>

      <PipelineToolbar
        pageKey="customers"
        sortBy={sortBy}
        onSortChange={setSortBy}
        showWindowChips={false}
        showForecastToggle={false}
        sortOptions={[
          { key: 'created_desc', label: 'Recently created' },
          { key: 'updated_desc', label: 'Recently updated' },
          { key: 'value_desc',   label: 'Revenue: High → Low' },
          { key: 'value_asc',    label: 'Revenue: Low → High' },
        ]}
      />

      <SummaryCards
        testid="customers-summary"
        loading={summaryLoading}
        items={summary ? [
          {
            label: 'Total revenue', value: inr(summary.total_revenue), icon: CurrencyInr,
            tone: 'revenue',
            sub: 'from approved quotations',
            testid: 'summary-total-revenue',
          },
          {
            label: 'Active customers', value: summary.active_customer_count, icon: Users,
            sub: 'with visible leads',
            testid: 'summary-active-customers',
          },
          {
            label: 'Top customer',
            value: summary.top_customers?.[0]
              ? summary.top_customers[0].name.length > 18
                ? summary.top_customers[0].name.slice(0, 18) + '…'
                : summary.top_customers[0].name
              : '—',
            icon: Star,
            sub: summary.top_customers?.[0] ? inr(summary.top_customers[0].revenue) : 'no revenue yet',
            testid: 'summary-top-customer',
          },
        ] : []}
      />

      <div className="mb-6">
        <div className="relative">
          <MagnifyingGlass size={20} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[#6D8E87]" />
          <input
            type="text"
            placeholder="Search by name, code, POC, email, GST, city, phone…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-12 pr-4 py-3 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] focus:border-[#00bfb3] outline-none"
            data-testid="customer-search-input"
          />
        </div>
      </div>

      <div className="flex justify-end mb-3">
        <ViewToggle mode={viewMode} onChange={setViewMode} testIdPrefix="customers-view" />
      </div>

      {viewMode === 'list' && (
      <div className="medical-card overflow-x-auto pb-24">
        <table className="w-full min-w-[900px]" data-testid="customers-table">
          <thead className="bg-[#F4F9F7]">
            <tr>
              {canBulkDelete && (
                <th className="text-left px-4 py-3 w-8">
                  <button
                    type="button"
                    onClick={selection.toggleAll}
                    data-testid="customers-bulk-select-all-toggle"
                    aria-label="Select all customers"
                  >
                    {selection.isAllSelected
                      ? <CheckSquare size={20} weight="fill" className="text-red-600" />
                      : <Square size={20} className={selection.isSomeSelected ? 'text-red-600' : 'text-[#6D8E87]'} />}
                  </button>
                </th>
              )}
              <th className="text-left px-6 py-3 text-xs uppercase tracking-[0.1em] text-[#3B5A54]">Code</th>
              <th className="text-left px-6 py-3 text-xs uppercase tracking-[0.1em] text-[#3B5A54]">Organisation</th>
              <th className="text-left px-6 py-3 text-xs uppercase tracking-[0.1em] text-[#3B5A54]">POC</th>
              <th className="text-left px-6 py-3 text-xs uppercase tracking-[0.1em] text-[#3B5A54]">Contact</th>
              <th className="text-left px-6 py-3 text-xs uppercase tracking-[0.1em] text-[#3B5A54]">GST</th>
              <th className="text-left px-6 py-3 text-xs uppercase tracking-[0.1em] text-[#3B5A54]">City</th>
              <th className="text-left px-6 py-3 text-xs uppercase tracking-[0.1em] text-[#3B5A54]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredCustomers.map((customer) => (
              <tr key={customer.id} className={`border-t border-[#D5E8E4] hover:bg-[#F4F9F7] ${selection.isSelected(customer.id) ? 'bg-red-50' : ''}`} data-testid={`customer-row-${customer.id}`}>
                {canBulkDelete && (
                  <td className="px-4 py-4 w-8">
                    <button
                      type="button"
                      onClick={() => selection.toggle(customer.id)}
                      data-testid={`customer-bulk-checkbox-${customer.id}`}
                      aria-label="Select customer"
                    >
                      {selection.isSelected(customer.id)
                        ? <CheckSquare size={18} weight="fill" className="text-red-600" />
                        : <Square size={18} className="text-[#6D8E87]" />}
                    </button>
                  </td>
                )}
                <td className="px-6 py-4">
                  <span className="inline-block px-3 py-1 bg-[#E2F3F0] text-[#00a69c] text-sm font-bold rounded-md" data-testid={`customer-code-${customer.id}`}>
                    {customer.customer_code}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <div className="font-medium text-[#052A23]">{customer.organisation_name}</div>
                  <div className="flex items-center gap-2 mt-0.5">
                    {customer.industry_type && <span className="text-xs text-[#6D8E87]">{customer.industry_type}</span>}
                    {customer.customer_role && customer.customer_role !== 'End Buyer' && (
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${customer.customer_role === 'Dealer' ? 'bg-amber-100 text-amber-800' : 'bg-purple-100 text-purple-800'}`} data-testid={`cust-role-${customer.id}`}>
                        {customer.customer_role}
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-[#052A23]">{customer.poc_name}</div>
                  {customer.poc_designation && <div className="text-xs text-[#6D8E87]">{customer.poc_designation}</div>}
                </td>
                <td className="px-6 py-4 text-sm text-[#3B5A54]">
                  <div className="flex items-center gap-1"><Phone size={14} />{customer.phone}</div>
                  <div className="flex items-center gap-1 text-xs text-[#6D8E87]"><Envelope size={12} />{customer.email}</div>
                </td>
                <td className="px-6 py-4 text-sm text-[#3B5A54]">{customer.gst_number || '-'}</td>
                <td className="px-6 py-4 text-sm text-[#3B5A54]">{customer.city || '-'}</td>
                <td className="px-6 py-4">
                  <div className="flex gap-2">
                    <button
                      onClick={() => navigate(`/customers/${customer.id}`)}
                      className="w-8 h-8 flex items-center justify-center text-[#00a69c] hover:bg-[#E2F3F0] rounded-lg transition"
                      data-testid={`view-customer-${customer.id}`}
                    >
                      <Eye size={18} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {filteredCustomers.length === 0 && (
              <tr>
                <td colSpan={canBulkDelete ? 8 : 7}>
                  <EmptyState title="No customers found" hint="Add your first customer, or adjust the search and filters." testid="customers-empty" />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      )}

      {viewMode === 'cards' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pb-24" data-testid="customers-grid">
          {filteredCustomers.length === 0 ? (
            <div className="col-span-full medical-card"><EmptyState title="No customers found" hint="Add your first customer, or adjust the search and filters." testid="customers-empty-cards" /></div>
          ) : filteredCustomers.map((customer) => (
            <div
              key={customer.id}
              className={`medical-card p-5 hover:shadow-lg transition cursor-pointer ${selection.isSelected(customer.id) ? 'ring-2 ring-red-400' : ''}`}
              onClick={() => navigate(`/customers/${customer.id}`)}
              data-testid={`customer-card-${customer.id}`}
            >
              <div className="flex items-start justify-between mb-2">
                <span className="inline-block px-3 py-1 bg-[#E2F3F0] text-[#00a69c] text-xs font-bold rounded-md">{customer.customer_code}</span>
                {canBulkDelete && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); selection.toggle(customer.id); }}
                    aria-label="Select customer"
                    data-testid={`customer-card-checkbox-${customer.id}`}
                  >
                    {selection.isSelected(customer.id)
                      ? <CheckSquare size={18} weight="fill" className="text-red-600" />
                      : <Square size={18} className="text-[#6D8E87]" />}
                  </button>
                )}
              </div>
              <h3 className="font-semibold text-[#052A23] text-lg leading-tight" style={{ fontFamily: 'Outfit' }}>{customer.organisation_name}</h3>
              <div className="flex items-center gap-2 mb-2">
                {customer.industry_type && <p className="text-xs text-[#6D8E87]">{customer.industry_type}</p>}
                {customer.customer_role && customer.customer_role !== 'End Buyer' && (
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${customer.customer_role === 'Dealer' ? 'bg-amber-100 text-amber-800' : 'bg-purple-100 text-purple-800'}`} data-testid={`cust-role-card-${customer.id}`}>
                    {customer.customer_role}
                  </span>
                )}
              </div>
              <div className="text-sm text-[#3B5A54] space-y-1 mt-3">
                <div className="flex items-center gap-2"><Buildings size={14} className="text-[#6D8E87]" /> {customer.city || '—'}</div>
                <div className="flex items-center gap-2"><Phone size={14} className="text-[#6D8E87]" /> {customer.phone || '—'}</div>
                <div className="flex items-center gap-2 truncate"><Envelope size={14} className="text-[#6D8E87]" /> <span className="truncate">{customer.email || '—'}</span></div>
                <div className="text-xs text-[#6D8E87] mt-2">GST: {customer.gst_number || '—'}</div>
                <div className="text-xs text-[#6D8E87]">POC: {customer.poc_name || '—'}{customer.poc_designation ? ` · ${customer.poc_designation}` : ''}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Customer Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#052A23]" style={{ fontFamily: 'Outfit' }}>
              Add New Customer
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4" data-testid="add-customer-form">
            {/* GST Number - Mandatory with verification OR Exemption Code */}
            <div className="p-4 bg-[#F4F9F7] rounded-lg border border-[#D5E8E4]">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-[#3B5A54]">
                  {noGstMode ? 'GST Exemption Code *' : 'GST Number *'}
                </label>
                <button
                  type="button"
                  onClick={() => {
                    setNoGstMode(!noGstMode);
                    setFormData(prev => ({ ...prev, gst_number: '', gst_exemption_code: '' }));
                    setGstStatus(null);
                    setCodeCheck(null);
                  }}
                  className="text-xs font-medium text-[#00a69c] hover:underline"
                  data-testid="toggle-no-gst-btn"
                >
                  {noGstMode ? 'Enter GST Number instead' : 'No GST Available?'}
                </button>
              </div>

              {!noGstMode ? (
                <>
                  <div className="flex gap-2">
                    <input type="text" required value={formData.gst_number}
                      onChange={(e) => {
                        const val = e.target.value.toUpperCase();
                        setFormData({ ...formData, gst_number: val });
                        setGstStatus(null);
                      }}
                      className="flex-1 px-4 py-2 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none uppercase"
                      placeholder="e.g., 33AADCT6419N1ZC" maxLength={15} data-testid="gst-number-input" />
                    <button type="button" onClick={() => verifyGST(formData.gst_number)}
                      disabled={gstVerifying || formData.gst_number.length < 15}
                      className="px-4 py-2 bg-[#00a69c] text-white rounded-lg text-sm font-medium hover:bg-[#008f86] transition disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                      data-testid="verify-gst-btn">
                      {gstVerifying ? 'Verifying...' : 'Verify GST'}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex gap-2">
                    <input type="text" required value={formData.gst_exemption_code}
                      onChange={(e) => {
                        const val = e.target.value.toUpperCase();
                        setFormData({ ...formData, gst_exemption_code: val });
                        setCodeCheck(null);
                      }}
                      className="flex-1 px-4 py-2 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none uppercase font-mono tracking-wider"
                      placeholder="Paste 8-char code from your Notifications"
                      maxLength={12}
                      data-testid="gst-exemption-code-input" />
                    <button type="button" onClick={validateExemptionCode}
                      disabled={!formData.gst_exemption_code || formData.gst_exemption_code.length < 6}
                      className="px-4 py-2 bg-[#00a69c] text-white rounded-lg text-sm font-medium hover:bg-[#008f86] transition disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                      data-testid="validate-code-btn">
                      Validate Code
                    </button>
                  </div>
                  <div className="mt-2 flex items-center justify-between">
                    <p className="text-xs text-[#6D8E87]">
                      Don't have a code yet?
                    </p>
                    <button
                      type="button"
                      onClick={() => setShowExemptionModal(true)}
                      className="text-xs font-semibold text-[#00a69c] hover:underline"
                      data-testid="request-exemption-btn"
                    >
                      Request GST Exemption →
                    </button>
                  </div>
                  {codeCheck && (
                    <div
                      className={`mt-2 p-3 rounded-lg text-sm ${codeCheck.valid ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'}`}
                      data-testid="code-validation-status"
                    >
                      {codeCheck.valid ? (
                        <span>✓ Code valid for: <strong>{codeCheck.organisation_name}</strong></span>
                      ) : (
                        <span>✗ {codeCheck.reason}</span>
                      )}
                    </div>
                  )}
                </>
              )}
              {!noGstMode && gstStatus && (
                <div className={`mt-2 p-3 rounded-lg text-sm ${gstStatus.valid ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`} data-testid="gst-status">
                  {gstStatus.valid ? (
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-green-700">Valid GSTIN</span>
                        {gstStatus.status && <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">{gstStatus.status}</span>}
                      </div>
                      {gstStatus.legal_name && <div className="text-green-700 font-medium">{gstStatus.legal_name}</div>}
                      {gstStatus.trade_name && gstStatus.trade_name !== gstStatus.legal_name && (
                        <div className="text-green-600 text-xs">Trade Name / Proprietor: {gstStatus.trade_name}</div>
                      )}
                      <div className="text-xs text-green-600 mt-1">State: {gstStatus.state} | PAN: {gstStatus.pan}
                        {gstStatus.registration_date && <span> | Registered: {gstStatus.registration_date}</span>}
                      </div>
                      {(gstStatus.legal_name || gstStatus.trade_name || gstStatus.address) && (
                        <div className="mt-1 text-xs text-green-500 italic">
                          Auto-filled: {[
                            gstStatus.legal_name && 'Organisation Name',
                            gstStatus.trade_name && gstStatus.trade_name !== gstStatus.legal_name && 'MD/CEO Name',
                            gstStatus.address && 'Address',
                            'State', 'PAN'
                          ].filter(Boolean).join(', ')}
                        </div>
                      )}

                      {/* Filing Status Section */}
                      {gstStatus.filing_status && gstStatus.filing_status.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-green-200" data-testid="filing-status-section">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="font-semibold text-green-800 text-xs">GST Return Filing Status</span>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              gstStatus.filing_health === 'good' ? 'bg-green-100 text-green-700' :
                              gstStatus.filing_health === 'warning' ? 'bg-amber-100 text-amber-700' :
                              gstStatus.filing_health === 'poor' ? 'bg-red-100 text-red-700' :
                              'bg-gray-100 text-gray-600'
                            }`} data-testid="filing-health-badge">
                              {gstStatus.filing_health === 'good' ? 'COMPLIANT' :
                               gstStatus.filing_health === 'warning' ? 'PARTIAL' :
                               gstStatus.filing_health === 'poor' ? 'NON-COMPLIANT' : 'UNKNOWN'}
                            </span>
                          </div>
                          {gstStatus.last_filing_date && (
                            <div className="text-xs text-green-700 mb-2">Last Filing: {gstStatus.last_filing_date}</div>
                          )}
                          <div className="grid grid-cols-1 gap-1">
                            {gstStatus.filing_status.map((f, i) => (
                              <div key={i} className="flex items-center justify-between text-xs py-1 px-2 bg-white rounded" data-testid={`filing-row-${i}`}>
                                <span className="font-medium text-green-800">{f.return_type}</span>
                                <span className="text-green-600">{f.period}</span>
                                <span className={`px-2 py-0.5 rounded-full font-medium ${
                                  f.status?.toLowerCase() === 'filed' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'
                                }`}>{f.status || 'N/A'}</span>
                                {f.date_of_filing && <span className="text-green-500">{f.date_of_filing}</span>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* No filing data message */}
                      {(!gstStatus.filing_status || gstStatus.filing_status.length === 0) && (
                        <div className="mt-3 pt-3 border-t border-green-200">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-green-800 text-xs">GST Return Filing Status</span>
                            <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs font-medium" data-testid="filing-health-badge">
                              DATA UNAVAILABLE
                            </span>
                          </div>
                          <p className="text-xs text-green-600 mt-1">Filing data not available from the free API. GSTIN format and state are verified.</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <span className="text-red-700">{gstStatus.error}</span>
                  )}
                </div>
              )}
            </div>

            {/* Organisation Info */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#3B5A54] mb-1">Organisation Name *</label>
                <input type="text" required value={formData.organisation_name}
                  onChange={(e) => setFormData({ ...formData, organisation_name: e.target.value })}
                  className="w-full px-4 py-2 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none"
                  data-testid="org-name-input" />
                {gstStatus?.valid && gstStatus.legal_name && (
                  <p className="text-xs text-green-600 mt-1">Auto-filled from GSTIN</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-[#3B5A54] mb-1">MD/CEO Name</label>
                <input type="text" value={formData.md_ceo_name}
                  onChange={(e) => setFormData({ ...formData, md_ceo_name: e.target.value })}
                  className="w-full px-4 py-2 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none"
                  data-testid="md-ceo-input" />
                {gstStatus?.valid && gstStatus.trade_name && gstStatus.trade_name !== gstStatus.legal_name && (
                  <p className="text-xs text-green-600 mt-1">Auto-filled from Trade Name</p>
                )}
              </div>
            </div>

            {/* POC Info */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#3B5A54] mb-1">POC Name *</label>
                <input type="text" required value={formData.poc_name}
                  onChange={(e) => setFormData({ ...formData, poc_name: e.target.value })}
                  className="w-full px-4 py-2 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none"
                  data-testid="poc-name-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#3B5A54] mb-1">POC Designation *</label>
                <input type="text" required value={formData.poc_designation}
                  onChange={(e) => setFormData({ ...formData, poc_designation: e.target.value })}
                  className="w-full px-4 py-2 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none"
                  data-testid="poc-designation-input" />
              </div>
            </div>

            {/* Contact Details */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#3B5A54] mb-1">Phone *</label>
                <input type="tel" required value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-2 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none"
                  data-testid="customer-phone-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#3B5A54] mb-1">Email *</label>
                <input type="email" required value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-2 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none"
                  data-testid="customer-email-input" />
              </div>
            </div>

            {/* Address */}
            <div>
              <label className="block text-sm font-medium text-[#3B5A54] mb-1">Address *</label>
              <textarea required value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="w-full px-4 py-2 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none"
                rows={2} data-testid="customer-address-input" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#3B5A54] mb-1">Country *</label>
                <select
                  value={formData.country || 'India'}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value, state: e.target.value === 'India' ? formData.state : '' })}
                  className="w-full px-4 py-2 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none bg-white"
                  required data-testid="customer-country-input"
                >
                  <option value="India">India</option>
                  <option value="United States">United States</option>
                  <option value="United Kingdom">United Kingdom</option>
                  <option value="United Arab Emirates">United Arab Emirates</option>
                  <option value="Singapore">Singapore</option>
                  <option value="Australia">Australia</option>
                  <option value="Canada">Canada</option>
                  <option value="Germany">Germany</option>
                  <option value="France">France</option>
                  <option value="Japan">Japan</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#3B5A54] mb-1">State {formData.country === 'India' ? '*' : ''}</label>
                {formData.country === 'India' ? (
                  <select
                    value={formData.state}
                    onChange={(e) => handleStateChange(e.target.value)}
                    className="w-full px-4 py-2 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none bg-white"
                    required data-testid="customer-state-input"
                  >
                    <option value="">Select State</option>
                    {Object.entries(STATES_BY_REGION).map(([region, states]) => (
                      <optgroup key={region} label={region}>
                        {states.map(s => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    className="w-full px-4 py-2 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none"
                    placeholder="State / Province / Region"
                    data-testid="customer-state-input"
                  />
                )}
                {gstStatus?.valid && gstStatus.state && formData.state === gstStatus.state && (
                  <p className="text-xs text-green-600 mt-1">Auto-filled from GSTIN</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-[#3B5A54] mb-1">City</label>
                <input type="text" value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  className="w-full px-4 py-2 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none"
                  placeholder="Enter city name" data-testid="customer-city-input" />
              </div>
            </div>

            {/* Tax & Legal */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#3B5A54] mb-1">PAN Number</label>
                <input type="text" value={formData.pan_number}
                  onChange={(e) => setFormData({ ...formData, pan_number: e.target.value.toUpperCase() })}
                  className="w-full px-4 py-2 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none uppercase"
                  placeholder="e.g., AAAPA1234A" maxLength={10} data-testid="pan-number-input" />
                {gstStatus?.valid && gstStatus.pan && formData.pan_number === gstStatus.pan && (
                  <p className="text-xs text-green-600 mt-1">Auto-filled from GSTIN</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#3B5A54] mb-1">Industry Type</label>
                <input type="text" value={formData.industry_type}
                  onChange={(e) => setFormData({ ...formData, industry_type: e.target.value })}
                  className="w-full px-4 py-2 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none"
                  data-testid="industry-type-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#3B5A54] mb-1">Customer Role</label>
                <select value={formData.customer_role || 'End Buyer'}
                  onChange={(e) => setFormData({ ...formData, customer_role: e.target.value })}
                  className="w-full px-4 py-2 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none bg-white"
                  data-testid="customer-role-select">
                  <option value="End Buyer">End Buyer</option>
                  <option value="Dealer">Dealer</option>
                  <option value="Liaison Agent">Liaison Agent</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#3B5A54] mb-1">Website</label>
                <input type="text" value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  className="w-full px-4 py-2 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none"
                  data-testid="website-input" />
              </div>
            </div>

            {/* Bank Accounts */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-[#3B5A54]">Bank Account Details</label>
                <button type="button" onClick={addBankAccount}
                  className="text-xs text-[#00a69c] hover:underline font-medium" data-testid="add-bank-account-btn">
                  + Add Bank Account
                </button>
              </div>
              <p className="text-xs text-[#6D8E87] mb-3">First bank account is auto-approved. Additional accounts require VP approval.</p>
              {formData.bank_accounts.map((ba, index) => (
                <div key={index} className="p-3 border border-[#D5E8E4] rounded-lg mb-3 bg-[#FAFFFE]" data-testid={`bank-account-${index}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-[#00a69c]">Bank Account {index + 1}{index > 0 ? ' (Requires VP Approval)' : ''}</span>
                    {index > 0 && (
                      <button type="button" onClick={() => removeBankAccount(index)}
                        className="text-xs text-red-500 hover:underline">Remove</button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <input type="text" placeholder="Bank Name" value={ba.bank_name}
                      onChange={(e) => updateBankAccount(index, 'bank_name', e.target.value)}
                      className="px-3 py-2 border border-[#D5E8E4] rounded-lg text-sm focus:ring-2 focus:ring-[#00bfb3] outline-none"
                      data-testid={`bank-name-${index}`} />
                    <input type="text" placeholder="Account Number" value={ba.account_number}
                      onChange={(e) => updateBankAccount(index, 'account_number', e.target.value)}
                      className="px-3 py-2 border border-[#D5E8E4] rounded-lg text-sm focus:ring-2 focus:ring-[#00bfb3] outline-none"
                      data-testid={`account-number-${index}`} />
                    <input type="text" placeholder="IFSC Code" value={ba.ifsc_code}
                      onChange={(e) => updateBankAccount(index, 'ifsc_code', e.target.value)}
                      className="px-3 py-2 border border-[#D5E8E4] rounded-lg text-sm focus:ring-2 focus:ring-[#00bfb3] outline-none"
                      data-testid={`ifsc-code-${index}`} />
                    <input type="text" placeholder="Branch" value={ba.branch}
                      onChange={(e) => updateBankAccount(index, 'branch', e.target.value)}
                      className="px-3 py-2 border border-[#D5E8E4] rounded-lg text-sm focus:ring-2 focus:ring-[#00bfb3] outline-none"
                      data-testid={`branch-${index}`} />
                  </div>
                </div>
              ))}
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-[#3B5A54] mb-1">Notes</label>
              <textarea value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="w-full px-4 py-2 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none"
                rows={2} data-testid="customer-notes-input" />
            </div>

            <div className="flex gap-3 pt-4">
              <button type="button" onClick={() => { setShowModal(false); setFormData(emptyForm); }}
                className="flex-1 px-6 py-3 border border-[#D5E8E4] rounded-lg hover:bg-gray-50 transition" data-testid="cancel-customer-btn">
                Cancel
              </button>
              <button type="submit"
                className="flex-1 btn-primary" data-testid="submit-customer-btn">
                Create Customer
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      <BulkUploadModal open={showBulkModal} onClose={() => setShowBulkModal(false)}
        title="Bulk Upload Customers" exportUrl="/customers-export/csv" importUrl="/customers-import/bulk"
        templateName="customers_template.csv" onSuccess={fetchCustomers}
        instructions="CSV with columns: organisation_name, md_ceo_name, poc_name, poc_designation, phone, email, address, city, state, gst_number, pan_number, industry_type, bank_name, account_number, ifsc_code, branch. Organisation name is required." />

      <BulkActionBar count={selection.count} onClear={selection.clear} testid="customers-bulk-action-bar">
        {canBulkDelete && (
          <button
            type="button"
            onClick={() => setShowBulkDeleteDialog(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-red-600 hover:bg-red-700 rounded-full transition"
            data-testid="customers-bulk-delete-btn"
          >
            <Trash size={14} weight="bold" /> Delete
          </button>
        )}
      </BulkActionBar>
      <BulkDeleteDialog
        open={showBulkDeleteDialog}
        onClose={() => setShowBulkDeleteDialog(false)}
        onConfirm={submitBulkDelete}
        entityLabel="customer" entityLabelPlural="customers"
        count={selection.count}
        loading={bulkDeleting}
        extraWarning={<p className="mt-2 text-xs">Any linked leads or quotations will be unlinked (not deleted).</p>}
      />

      {/* GST Exemption Request Modal */}
      <Dialog open={showExemptionModal} onOpenChange={setShowExemptionModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#052A23]" style={{ fontFamily: 'Outfit' }}>
              Request GST Exemption
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4 space-y-4" data-testid="gst-exemption-request-form">
            <p className="text-sm text-[#6D8E87]">
              This request will be sent to your immediate supervisor. Once approved,
              you'll receive a single-use 8-character bypass code in your Notifications.
            </p>
            <div>
              <label className="block text-sm font-medium text-[#3B5A54] mb-1">Customer Organisation Name</label>
              <input
                type="text"
                value={formData.organisation_name}
                onChange={(e) => setFormData({ ...formData, organisation_name: e.target.value })}
                className="w-full px-3 py-2 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none"
                placeholder="e.g., XYZ Traders"
                data-testid="exemption-org-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#3B5A54] mb-1">Reason for No GST *</label>
              <select
                value={exemptionForm.reason}
                onChange={(e) => setExemptionForm({ ...exemptionForm, reason: e.target.value })}
                className="w-full px-3 py-2 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none bg-white"
                data-testid="exemption-reason-select"
              >
                <option value="">Select reason</option>
                <option value="Unregistered Dealer (turnover below threshold)">Unregistered Dealer (URD)</option>
                <option value="Composition Scheme">Composition Scheme</option>
                <option value="Government / PSU buyer">Government / PSU buyer</option>
                <option value="Foreign / Export customer">Foreign / Export customer</option>
                <option value="SEZ unit">SEZ unit</option>
                <option value="Charitable institution / Trust">Charitable institution / Trust</option>
                <option value="Other">Other (specify in notes)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#3B5A54] mb-1">Additional Notes</label>
              <textarea
                value={exemptionForm.notes}
                onChange={(e) => setExemptionForm({ ...exemptionForm, notes: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none"
                placeholder="Any context for your supervisor (optional)"
                data-testid="exemption-notes-input"
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowExemptionModal(false)}
                className="px-4 py-2 border border-[#D5E8E4] rounded-lg text-sm font-medium text-[#3B5A54] hover:bg-[#F4F9F7]"
                data-testid="cancel-exemption-btn"
              >Cancel</button>
              <button
                onClick={submitExemptionRequest}
                disabled={submittingExemption || !exemptionForm.reason}
                className="px-4 py-2 bg-[#00a69c] text-white rounded-lg text-sm font-medium hover:bg-[#008f86] disabled:opacity-50"
                data-testid="submit-exemption-btn"
              >
                {submittingExemption ? 'Submitting...' : 'Submit Request'}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Customers;
